import { createPdfHelpers, uploadAndRegisterPdf } from './gestion-tecnica-documentos'
import { NioshAssessmentRow } from './gestion-tecnica-niosh'

// Genera el reporte técnico PDF de una evaluación NIOSH, siguiendo el mismo
// patrón que generateRebaReportPDF(): createPdfHelpers() para el flujo de
// texto + uploadAndRegisterPdf() para subir el blob y registrarlo en
// sso_documents. A diferencia de REBA/RULA (una sola postura), NIOSH es
// multitarea: se recorre evaluacion.tareas describiendo cada tarea y luego
// se documenta el Índice de Levantamiento Compuesto que las combina.
//
// checklist_item_id: 'GT-06' — mismo ítem genérico ya confirmado para
// RULA y REBA (compliance-engine.ts: "Informe de evaluación de riesgos
// ergonómicos y psicosociales", Decisión 584 Art. 11, DE 255 (2024)
// Art. 44/45/46, AM 196 (2024)).
export async function generateNioshReportPDF(
  evaluacion: NioshAssessmentRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { txt, hline, doc } = helpers

  const { tareas, resultado_compuesto: resultadoCompuesto } = evaluacion

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Evaluación Ergonómica — Ecuación de NIOSH', 12, 'bold', 'center', 0, 6)
  txt(evaluacion.descripcion, 11, 'bold', 'center', 0, 6)
  txt('Fuente metodológica: NIOSH (Revised Lifting Equation — Waters, Putz-Anderson & Garg, 1994)', 9, 'italic', 'left', 0, 3)
  hline()

  // b. Datos de entrada y resultado por tarea
  txt('Datos de entrada por tarea', 11, 'bold', 'left', 0, 4)

  tareas.forEach((tarea, index) => {
    if (index > 0) hline()

    txt(`Tarea: ${tarea.id}`, 10, 'bold', 'left', 0, 3)

    const { origen, destino, frecuencia, duracion, peso, unaMano, multiplesTrabajadores } = tarea.input

    txt(`Origen — H: ${origen.H}cm, V: ${origen.V}cm, A: ${origen.A}°, agarre: ${origen.agarre}`, 9, 'normal', 'left', 4, 2)

    if (destino) {
      txt(`Destino — H: ${destino.H}cm, V: ${destino.V}cm, A: ${destino.A}°, agarre: ${destino.agarre}`, 9, 'normal', 'left', 4, 2)
      txt('Con control significativo de carga en destino', 9, 'italic', 'left', 4, 2)
    }

    txt(`Frecuencia: ${frecuencia} elev/min — Duración: ${duracion}`, 9, 'normal', 'left', 4, 2)
    txt(`Peso: ${peso}kg — Una mano: ${unaMano ? 'sí' : 'no'} — Múltiples trabajadores: ${multiplesTrabajadores ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 4)

    txt('Resultado de la tarea', 10, 'bold', 'left', 0, 3)
    txt(`D: ${tarea.resultado.D}cm — RWL origen: ${tarea.resultado.rwlOrigen.toFixed(2)}kg${tarea.resultado.rwlDestino !== null ? ` — RWL destino: ${tarea.resultado.rwlDestino.toFixed(2)}kg` : ''}`, 9, 'normal', 'left', 4, 2)
    txt(`RWL final: ${tarea.resultado.rwlFinal.toFixed(2)}kg — LI: ${tarea.resultado.li.toFixed(2)} — Nivel de riesgo: ${tarea.resultado.nivelRiesgo}`, 9, 'normal', 'left', 4, 4)
  })

  hline()

  // c. Resultado del Índice de Levantamiento Compuesto
  txt('Resultado del Índice de Levantamiento Compuesto', 11, 'bold', 'left', 0, 4)

  resultadoCompuesto.ordenPorLI.forEach(tarea => {
    txt(
      `${tarea.id} — LI propio: ${tarea.liPropio.toFixed(2)}, frecuencia acumulada: ${tarea.frecuenciaAcumuladaHastaAqui}, LI a frecuencia acumulada: ${tarea.liAFrecuenciaAcumulada.toFixed(2)}, incremento: ${tarea.incremento.toFixed(2)}`,
      9, 'normal', 'left', 4, 2
    )
  })

  txt(`Índice de Levantamiento Compuesto (ILC): ${resultadoCompuesto.ilc.toFixed(2)}`, 11, 'bold', 'left', 0, 3)
  txt(`Nivel de riesgo: ${resultadoCompuesto.nivelRiesgo}`, 10, 'bold', 'left', 0, 4)

  txt(
    `Métodos alternativos de referencia (no recomendados): suma de riesgos=${resultadoCompuesto.sumaDeRiesgos.toFixed(2)}, ` +
    `riesgo promedio=${resultadoCompuesto.riesgoPromedio.toFixed(2)}, mayor riesgo=${resultadoCompuesto.mayorRiesgo.toFixed(2)}`,
    9, 'italic', 'left', 0, 4
  )
  hline()

  // d. Disclaimer — mismo criterio que RULA/REBA: el PDF documenta
  // exactamente lo que ve el usuario en pantalla, no una versión distinta.
  txt(
    'Ecuación de NIOSH según NIOSH [1994], Applications Manual for the Revised NIOSH Lifting Equation, DHHS Publication No. 94-110. ' +
    'Este reporte no reemplaza el juicio de un profesional de ergonomía para el rediseño de la tarea.',
    8, 'italic', 'left', 0, 4
  )

  const blob = doc.output('blob')
  const filename = `NIOSH_${evaluacion.descripcion.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${evaluacion.id}-niosh`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte NIOSH - ${evaluacion.descripcion}`,
    description: 'Generado automáticamente desde Metodología NIOSH',
    checklist_item_id: 'GT-06',
    uploaded_by: uploadedBy,
  })
}
